import './upload-result.scss';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { getUserData, type ResultType } from '../../../constants/global';
import { useGlobalContext } from '../../../context/global';
import { isUnitLocked, useUnitLock } from '../../../hooks/useUnitLock';
import { SAGA_LEVEL_BY_RESULT_TYPE } from '../../../interface/saga-status';
import type { IElectionDivision, IGetElectionOfficial } from '../../../interface/state/IElectionState';
import { type IIrevElectionSubmissionResponse } from '../../../interface/state/IRev';
import { agentPresenceEnabled, type PresenceSaveState, savePresence } from '../../../lib/agent-presence-api';
import { puCancellationEnabled } from '../../../lib/cancellation-api';
import { resultApiVersion } from '../../../lib/result-submission-api';
import { TypeButton } from '../../utils/button';
import { CopyValue } from '../../utils/copy';
import { handleScrollRightSection } from '../../utils/helper';
import { Hvc } from '../../utils/hooks';
import { BackArrowSVG, RightArrowSVG, SaveDraftSVG } from '../../utils/svgs';
import { CancelledUnitNotice } from '../pu-cancellation/cancelled-unit-notice';
import { UnitLockedPanel } from '../pu-cancellation/lock';
import ResultVideoCard from '../result-video/ResultVideoCard';
import type { IPartyScore } from '../result-video/ResultVideoRecorder';
import { SubmissionStatus } from '../saga-status/SubmissionStatus';
import { type IPresenceAnswer, tallyPresence } from './agent-presence';
import {
	defaultElectionData,
	getResults,
	useLGAResultDataManager,
	usePollingUnitResultDataManager,
	useStateResultDataManager,
	useWardResultDataManager,
} from './hooks';
import ParentCodesStage from './parent-codes';
import PartyVotesStage from './party-votes-stage';
import PollingAgentsStage from './polling-agents-stage';
import { PresenceSaveNotice } from './presence-save-notice';
import Preview from './preview';
import ResultSummaryStage from './result-summary';
import SubmitResultStatusStage from './submit-result-status';
import UploadResultStage from './upload-result';
import UploadResultStatusStage from './upload-result-status';
import { computeVoteBalance } from './vote-balance';
import VoteSummary from './vote-summary';

const UploadResult = () => {
	const { rsProps, actions, states, global, saveAsDraft, onStage, clearDraftLocal, clearDraft, getCodeProps, supportChat } =
		useGlobalContext();
	const bottomRef = useRef<HTMLDivElement>(null);
	// Captured from the submission response so the success screen can open the
	// completion certificate before get_ElectionOfficial is refetched.
	const [submittedRef, setSubmittedRef] = useState('');
	// Hard gate: a submission must carry at least one party score (the backend
	// rejects agent submissions without one — see api.command ValidateCreateRequest).
	const [resultsBlocked, setResultsBlocked] = useState(false);
	// The server's own rejection reason, kept on screen. A global toast is easy to miss in the
	// field, and the V2 create endpoints reject for reasons the agent can act on (most commonly
	// "a result already exists for this unit"). The thunks pass `validateStatus: () => true`
	// so onFailure receives `{ notice: <server message> }` rather than 'Something went wrong'.
	const [submitError, setSubmitError] = useState('');
	// Set when the agent confirms a discrepancy is real and on the sheet itself.
	const [balanceAcknowledged, setBalanceAcknowledged] = useState(false);
	// Polling agents are saved AFTER the result is accepted, by a separate api.core call that
	// can never affect the result. The snapshot lets "Retry" resend exactly what was submitted.
	const [presenceSave, setPresenceSave] = useState<PresenceSaveState>({ status: 'idle' });
	const presenceSnapshot = useRef<{ electionId: number; pollingUnitCode: string; answers: IPresenceAnswer[] } | null>(null);
	// What the result-video offer needs, captured at submit: Close clears the flow state, and the
	// offer (and any upload it starts) must not depend on it.
	const [videoSnapshot, setVideoSnapshot] = useState<{
		electionId: number;
		pollingUnitCode: string;
		pollingUnitName?: string;
		referenceId: string;
		partyScores: IPartyScore[];
		flagged: boolean;
	} | null>(null);

	const {
		state: {
			files,
			isPreview,
			partyVotes,
			selectedParentCode,
			selectedChildCode,
			partyVoteStage,
			stage,
			uploadedFiles,
			uploadedAt,
			draftStatus,
			resultType,
			parentCodes,
			resultSummary,
			childCodes,
			agentPresence,
			electionData,
		},
		updateState,
		clearAll,
	} = global;

	const as = rsProps?.data as {
		assignmentData: IElectionDivision[] | undefined;
		electionData: IGetElectionOfficial | undefined;
		autoSelectParent?: boolean;
	};

	const handleInitParentCode = () => {
		const resultType = rsProps?.resultType!;
		const parents =
			as?.assignmentData?.map((i) => ({
				code: i?.code,
				codeId: i.id,
				name: i.name,
				status: false,
			})) || [];
		updateState('parentCodes', parents);
		updateState('resultType', resultType);
		updateState('electionData', as?.electionData || defaultElectionData);

		// Deep-link: when a single parent is pre-supplied, skip the selection step
		// and land directly on the upload step for that entity.
		if (as?.autoSelectParent && parents.length === 1) {
			updateState('selectedParentCode', parents[0]);
			updateState('stage', 'Upload Result');
		}
	};

	// Only seed parent codes when there is assignment data to seed from. UploadResult stays
	// mounted (hidden) behind other right-section panels, whose rsProps.data carries no
	// assignmentData — without this guard handleInitParentCode would write a fresh empty
	// `parentCodes` array every render, and since the array is a new reference each time it
	// re-triggers this effect (parentCodes is a dep), causing an infinite update loop.
	useEffect(() => {
		if (!parentCodes.length && as?.assignmentData?.length) handleInitParentCode();
	}, [as?.assignmentData, parentCodes]);

	useEffect(() => {
		handleScrollRightSection(bottomRef);
		if (stage === 'Preview' && !isPreview) {
			updateState('isPreview', true);
		}
	}, [stage]);

	useEffect(() => {
		updateState(
			'isUpdate',
			(() => {
				if (uploadedFiles.length) {
					// if lenght of files is more than uploaded files
					// if uploaded files exists and theres movement in files
					return files.length !== uploadedFiles.length || !!files.length;
				}
				return false;
			})()
		);
	}, [files]);

	const onDoneSubmission = () => {
		setPresenceSave({ status: 'idle' });
		setVideoSnapshot(null);
		presenceSnapshot.current = null;
		clearAll();
		clearDraftLocal();
		clearDraft(selectedParentCode?.code);
		handleInitParentCode();
		updateState('stage', 'Electoral Division');
	};

	// Open the completion certificate for the just-submitted result. Switches the
	// active right-section panel (upload-result stays mounted, hidden, behind it).
	const openCertificate = () => {
		if (!submittedRef) return;
		rsProps?.callSection({
			action: 'view',
			component: 'completion-receipt',
			title: 'Certificate of Submission',
			max: true,
			resultType: resultType as ResultType,
			data: { referenceId: submittedRef },
		});
	};

	const results = getResults(states, partyVotes);

	// Auto-clear the gate once the agent records a party score.
	useEffect(() => {
		if (results.length && resultsBlocked) setResultsBlocked(false);
	}, [results.length, resultsBlocked]);

	// The result sheet's own arithmetic: the party sum against the declared Valid
	// Votes, plus the ballot account. Figures can legitimately disagree with the
	// sheet, so this warns and asks the agent to say so rather than blocking —
	// but nobody submits a transcription slip without noticing it.
	const balance = useMemo(
		() => computeVoteBalance({ partyVotes, resultSummary, childCodes, resultType }),
		[partyVotes, resultSummary, childCodes, resultType]
	);

	// Re-arm the gate whenever the figures move: an acknowledgement is about the
	// specific discrepancy the agent looked at, not a permanent opt-out.
	useEffect(() => {
		setBalanceAcknowledged(false);
	}, [balance.difference, balance.hasSheetIssue, balance.state]);

	const { data: stateResultData } = useStateResultDataManager({
		results,
	});

	const { data: lgaResultData } = useLGAResultDataManager({
		results,
	});

	const { data: wardResultData } = useWardResultDataManager({
		results,
	});

	const { data: pollingUnitResultData, missing: missingPuFields } = usePollingUnitResultDataManager({
		results,
	});
	// Only polling units can be missing lookup-sourced fields, and only on V2.
	const v2FieldsMissing = resultType === 'EC8A' ? missingPuFields : [];
	// A saga only exists for levels submitting through V2. On V1 there is nothing to observe,
	// so the pipeline view is not rendered at all rather than reporting "status unavailable".
	const sagaLevel =
		resultApiVersion(resultType as ResultType) === 'v2' ? SAGA_LEVEL_BY_RESULT_TYPE[resultType as ResultType] : undefined;

	const presenceOn = resultType === 'EC8A' && agentPresenceEnabled();
	const presenceTally = useMemo(() => {
		const roster = partyVotes.find((p) => p.id === selectedParentCode?.codeId)?.votes || [];
		return tallyPresence(
			agentPresence,
			roster.map((v) => v.partyId)
		);
	}, [agentPresence, partyVotes, selectedParentCode?.codeId]);

	const runPresenceSave = async () => {
		const snapshot = presenceSnapshot.current;
		if (!snapshot) return;
		setPresenceSave({ status: 'saving' });
		const outcome = await savePresence(snapshot);
		// Closed (and possibly restarted on another unit) while this was in flight.
		if (presenceSnapshot.current !== snapshot) return;
		setPresenceSave(outcome);
	};

	// Called once the result itself has been ACCEPTED. No answers → nothing to write, and the
	// stored record (if any) is left exactly as it is.
	const startPresenceSave = () => {
		if (!presenceOn || !agentPresence.length) return;
		presenceSnapshot.current = {
			electionId: Number(electionData?.data?.[0]?.electionId) || 0,
			pollingUnitCode: selectedParentCode?.code || '',
			answers: agentPresence,
		};
		void runPresenceSave();
	};

	// INEC cancelled this unit and the party has the report: nothing is uploaded until it is
	// reinstated. The success stage stays — a result accepted a moment before the lock synced
	// must still show its receipt.
	const ownElectionId = states?._election?.get_ElectionOfficial?.data?.find(
		(i) => i.userId === Number(getUserData().user?.UserId)
	)?.electionId;
	// Before the agent picks the unit (Electoral Division step) the selection is empty — a polling-unit
	// agent opens this panel for exactly one unit, so lock on that one from the first step.
	const lockedUnitCode = selectedParentCode?.code || (as?.assignmentData?.length === 1 ? as.assignmentData[0]?.code : undefined);
	const lock = useUnitLock(ownElectionId, lockedUnitCode, resultType === 'EC8A');
	const showLocked = lock.locked && stage !== 'Election Result Submission Status';
	const lockedMessage = 'INEC cancelled this polling unit. Your party must reinstate it before a result can be uploaded.';

	const onSubmit = () => {
		// Re-read at the moment of sending: the panel may have opened before the lock synced.
		if (resultType === 'EC8A' && isUnitLocked(ownElectionId, lockedUnitCode)) {
			setSubmitError(lockedMessage);
			return;
		}
		if (!results.length) {
			setResultsBlocked(true);
			return;
		}
		// Belt and braces behind the Preview gate: a draft restored offline can reach this point
		// with a payload the background lookups never filled in. Sending it would be accepted by
		// the server and produce a saga row with a blank ward.
		if (v2FieldsMissing.length) return;
		setSubmitError('');
		// `apiRespond` hands onFailure the notification payload, so the server's message is
		// `err.notice`. Fall back only when the server sent no message at all.
		const onFailure = (err: any) =>
			// A 409 UNIT_CANCELLED from the server's guard arrives here as its message, worded like the lock.
			setSubmitError(err?.notice || 'We couldn’t submit this result. Check your connection and try again.');
		const onSuccess = (res: IIrevElectionSubmissionResponse) => {
			const data = states?._election.get_ElectionOfficial.data;

			const obj: Partial<Record<ResultType, any>> = {
				EC8A: pollingUnitResultData,
				EC8B: wardResultData,
				EC8C: lgaResultData,
				EC8D: stateResultData,
			};

			const reqData = obj?.[resultType as ResultType];

			const assignmentData = data?.find((i) => i.electionId === reqData.election.id);

			if (!assignmentData) return;

			const assignment = assignmentData.assignment;

			actions?.update_ElectionOfficialById({
				onSuccess: () => {
					setSubmittedRef(res.referenceId);
					updateState('stage', 'Election Result Submission Status');
				},
				data: {
					id: assignmentData.id,
					referenceId: res.referenceId,
					userId: assignmentData.userId,
					electionId: assignmentData.electionId,
					election: assignmentData.election,
					name: assignmentData.name,
					assignment: {
						id: Number(assignment.id),
						code: assignment.code,
						name: assignment.name,
						resultType: assignment.resultType,
						isCompleted: true,
					},
				},
				id: assignmentData.id,
			});
		};
		if (resultType === 'EC8D') {
			actions?.push_IRevStateDataModel({
				data: stateResultData,
				onFailure,
				onSuccess: (data) => {
					actions?.get_IrevStateResultByRefId({
						referenceId: data.referenceId,
						onSuccess: () => {
							onSuccess(data);
						},
					});
				},
			});
		}
		if (resultType === 'EC8C') {
			actions?.push_IRevLGADataModel({
				data: lgaResultData,
				onFailure,
				onSuccess: (data) => {
					actions?.get_IrevLgaResultByRefId({
						referenceId: data.referenceId,
						onSuccess: () => {
							onSuccess(data);
						},
					});
				},
			});
		}
		if (resultType === 'EC8B') {
			actions?.push_IRevWardDataModel({
				data: wardResultData,
				onFailure,
				onSuccess: (data) => {
					actions?.get_IrevWardResultByRefId({
						referenceId: data.referenceId,
						onSuccess: () => {
							onSuccess(data);
						},
					});
				},
			});
		}
		if (resultType === 'EC8A') {
			actions?.push_IRevPollingUnitDataModel({
				data: pollingUnitResultData,
				onFailure,
				onSuccess: (data) => {
					startPresenceSave();
					setVideoSnapshot({
						electionId: Number(electionData?.data?.[0]?.electionId) || 0,
						pollingUnitCode: selectedParentCode?.code || '',
						pollingUnitName: selectedParentCode?.name,
						referenceId: data.referenceId,
						partyScores: results.map((r) => ({ name: r.partyName, votes: Number(r.votes) || 0 })),
						flagged: balanceAcknowledged,
					});
					actions?.get_IrevPollingUnitResultByRefId({
						referenceId: data.referenceId,
						onSuccess: () => {
							onSuccess(data);
						},
					});
				},
			});
		}
	};

	const getSubmitLoad = () =>
		states?._irev?.push_IRevDataModelLoading ||
		states?._irev?.push_IRevPollingUnitDataModelLoading ||
		states?._irev?.push_IRevWardDataModelLoading ||
		states?._irev?.push_IRevLGADataModelLoading ||
		states?._irev?.push_IRevStateDataModelLoading ||
		false;

	const submitLoad = getSubmitLoad();

	return (
		<div
			className='pb-4 mb-4 f-column-33 px-2'
			ref={bottomRef}
		>
			<div className='upload-result-header'>
				<div className='upload-result-header__top'>
					<div className='upload-result-header__left'>
						<Hvc
							removeDOM
							view={stage !== 'Electoral Division' && stage !== 'Election Result Submission Status'}
						>
							<button
								className='upload-result-header__back-btn'
								onClick={() => {
									if (stage === 'Upload Result') {
										onStage(global.state.isPreview ? 'Preview' : 'Electoral Division');
									} else if (stage === 'Upload Result Status') {
										onStage('Upload Result');
									} else if (stage === 'Result Summary') {
										onStage('Upload Result Status');
									} else if (stage === 'Party Vote Count') {
										onStage('Result Summary');
									} else if (stage === 'Party Vote Summary' || stage === 'Polling Agents') {
										onStage('Party Vote Count');
									} else if (stage === 'Preview') {
										onStage('Party Vote Summary');
									}
								}}
								type='button'
								aria-label='Go back'
							>
								<BackArrowSVG />
							</button>
						</Hvc>
						<h3 className='upload-result-header__title'>{stage}</h3>
					</div>
					<Hvc
						removeDOM
						view={stage !== 'Election Result Submission Status' && stage !== 'Electoral Division'}
					>
						<button
							className={`upload-result-header__save-btn${draftStatus ? ' upload-result-header__save-btn--saving' : ''}`}
							onClick={saveAsDraft}
							type='button'
							aria-label={draftStatus ? 'Saving draft...' : 'Save as draft'}
							title={draftStatus ? 'Saving...' : 'Save as Draft'}
						>
							<SaveDraftSVG />
						</button>
					</Hvc>
				</div>
				<Hvc
					removeDOM
					view={!!selectedParentCode?.code}
				>
					<div className='upload-result-header__assignment'>
						<div className='upload-result-header__assignment-badge'>
							<span className='upload-result-header__assignment-label'>Assigned Area</span>
							<div className='upload-result-header__assignment-value'>
								<span className='upload-result-header__assignment-name'>{selectedParentCode.name}</span>
								<span className='upload-result-header__assignment-code'>
									{selectedParentCode?.code}
									<CopyValue
										value={selectedParentCode?.code}
										label='code'
										size={14}
									/>
								</span>
							</div>
						</div>
						<Hvc
							removeDOM
							view={partyVoteStage === 'Party Votes' && !!selectedChildCode.name}
							className='upload-result-header__assignment-child'
						>
							<RightArrowSVG />
							<div className='upload-result-header__assignment-badge'>
								<span className='upload-result-header__assignment-name'>{selectedChildCode.name}</span>
								<span className='upload-result-header__assignment-code'>
									{selectedChildCode?.code}
									<CopyValue
										value={selectedChildCode?.code}
										label='code'
										size={14}
									/>
								</span>
							</div>
						</Hvc>
					</div>
				</Hvc>
			</div>
			{showLocked ? (
				<UnitLockedPanel
					cancelledAt={lock.cancelledAt}
					onMessageParty={
						supportChat
							? () => {
									rsProps?.addRightSectionHistory?.();
									rsProps?.callSection({
										action: 'view',
										component: 'support-chat',
										title: 'Support desk',
										data: {
											seedDraft: `INEC's cancellation of polling unit ${selectedParentCode?.code ?? ''} has been reversed. Please reinstate the unit so I can upload the result.`,
										},
									});
								}
							: undefined
					}
				/>
			) : null}
			{resultType === 'EC8A' && puCancellationEnabled() && stage !== 'Election Result Submission Status' && !showLocked ? (
				<CancelledUnitNotice
					electionId={ownElectionId}
					pollingUnitCode={selectedParentCode?.code}
				/>
			) : null}
			{showLocked ? null : (
				<>
					{/* Parent Code */}
					<Hvc
						removeDOM
						view={stage === 'Electoral Division'}
					>
						<ParentCodesStage />
					</Hvc>
					{/* Upload Result */}
					<Hvc
						removeDOM
						view={stage === 'Upload Result'}
						className='f-column-23 align-items-center w-100 py-2'
					>
						<UploadResultStage />
					</Hvc>
					{/* Upload Result Status */}
					<Hvc
						removeDOM
						view={stage === 'Upload Result Status'}
					>
						<UploadResultStatusStage
							onNext={() => {
								updateState('isUpdate', false);
								onStage('Result Summary');
							}}
							onView={() => {
								updateState('isUpdate', false);
								onStage('Upload Result');
							}}
							fileCount={uploadedFiles.length}
							uploadedAt={uploadedAt}
							assignmentName={selectedParentCode?.name}
							assignmentCode={selectedParentCode?.code}
						/>
					</Hvc>
					<Hvc
						removeDOM
						view={stage === 'Party Vote Summary'}
						className='f-column-33'
					>
						<VoteSummary />
						<TypeButton
							title='Done'
							buttonSize='small'
							onClick={() => onStage('Preview')}
						/>
					</Hvc>
					<Hvc
						removeDOM
						view={stage === 'Polling Agents'}
						className='f-column-33'
					>
						<PollingAgentsStage />
						<TypeButton
							title='Done'
							buttonSize='small'
							onClick={() => onStage('Preview')}
						/>
					</Hvc>
					<Hvc
						removeDOM
						view={stage === 'Result Summary'}
					>
						<ResultSummaryStage />
					</Hvc>
					<Hvc
						removeDOM
						view={stage === 'Party Vote Count'}
					>
						<PartyVotesStage />
					</Hvc>
					<Hvc
						removeDOM
						view={stage === 'Preview'}
					>
						<Preview
							onStage={onStage}
							onSubmit={onSubmit}
							load={submitLoad}
							numberofUploadedFile={uploadedFiles.length}
							resultsBlocked={resultsBlocked}
							balance={balance}
							acknowledged={balanceAcknowledged}
							onAcknowledge={setBalanceAcknowledged}
							submitError={submitError}
							missingFields={v2FieldsMissing}
							onRetryLookups={getCodeProps.handleCodes}
							lookupsLoading={getCodeProps.load}
							presence={presenceOn ? presenceTally : undefined}
						/>
					</Hvc>
				</>
			)}
			<Hvc
				removeDOM
				view={stage === 'Election Result Submission Status'}
				className='f-column-15'
			>
				{/*
					On V2 the submission starts a pipeline we can actually observe, so the static
					"no further action is required" card would be a claim we can't stand behind —
					it is exactly what hides a stalled submission. The live panel replaces it and
					derives its own headline. On V1 no saga exists, so the static card still tells
					the whole truth and stays.
				*/}
				<PresenceSaveNotice
					state={presenceSave}
					onRetry={() => void runPresenceSave()}
				/>
				{sagaLevel ? (
					<SubmissionStatus
						resultType={resultType as ResultType}
						electionId={global.state.electionData?.data?.[0]?.electionId}
						code={selectedParentCode?.code}
						referenceId={submittedRef}
						enabled={stage === 'Election Result Submission Status'}
						onCompare={openCertificate}
						onReupload={onDoneSubmission}
					/>
				) : (
					<SubmitResultStatusStage
						onClick={openCertificate}
						title='The Election Result has been submitted successfully'
						description='Your task has been completed, and no further action is required.
          Thank you for ensuring accuracy and fulfilling your
          responsibility.'
						btnTitle='View completion certificate'
					/>
				)}
				{/* No new video session opens on a locked unit; an upload already queued still finishes. */}
				{videoSnapshot && !lock.locked ? (
					<ResultVideoCard
						context='success'
						electionId={videoSnapshot.electionId}
						pollingUnitCode={videoSnapshot.pollingUnitCode}
						pollingUnitName={videoSnapshot.pollingUnitName}
						resultReferenceId={videoSnapshot.referenceId}
						partyScores={videoSnapshot.partyScores}
						flagged={videoSnapshot.flagged}
					/>
				) : null}
				<TypeButton
					title='Close'
					buttonSize='small'
					buttonType='outlined'
					className='w-100'
					onClick={onDoneSubmission}
				/>
			</Hvc>
			<div />
		</div>
	);
};

export default UploadResult;
